import React, { useState } from 'react';
import { Box, Button, Chip, Divider, Dialog, IconButton, Stack, Typography, Alert, useMediaQuery, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { conditionStatusLabel, refActionLabel, driftTypeLabel, trapCategoryLabel, conditionTypeLabel } from '../../utils/trlConstants';
import { canResolve, canHide, resolutionTarget, isSingleStep } from '../../utils/conditionWorkflow';
import { submitResolution, hideConditionFor } from '../../../../services/trlService';
import { notifyConditionEvent } from '../../../../services/conditionNotificationService';
import { ConditionHeader, SeverityChips } from './ConditionHeader';
import { ResolveDialog, InlineNotice } from './ConditionDialogs';

function when(ts) {
  if (!ts) return '';
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleString();
}

/**
 * Read-only "review" view of one OE Condition (P2-08): everything the record holds, the status
 * history, and the actions the viewer may take here — Mark as Resolved (owner / entrant while
 * Pending / admin, P2-12), Edit (entrant while Pending → entry form), Delete from my list (closed
 * items, P2-13). Review decisions stay on the L1/L2 queues.
 */
export function ConditionDetailDrawer({ condition, viewer, open, onClose, onChanged }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [resolveOpen, setResolveOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  if (!condition) return null;
  const c = condition;
  const mayResolve = canResolve(c, viewer);
  const mayEdit = c.status === 'submitted' && c.submittedBy === viewer?.uid;
  const mayHide = canHide(c);
  const direct = resolutionTarget(c.status) === 'resolved';

  const doResolve = async (note) => {
    setBusy(true);
    setNotice(null);
    try {
      const next = await submitResolution(c.id, { note, by: viewer.uid, byName: viewer.name, actingFor: viewer.actingFor, singleStep: isSingleStep(c, viewer.routing) });
      notifyConditionEvent(next === 'resolved' ? 'resolved' : 'resolution_submitted', c, viewer, { after: { status: next }, actedAs: 'resolved an OE Condition' });
      setResolveOpen(false);
      setNotice({ severity: 'success', message: next === 'resolved' ? 'Resolved — recorded as found and fixed.' : 'Resolution submitted — it goes to L1, then L2, for approval.' });
      if (onChanged) onChanged();
    } catch (err) {
      setNotice({ severity: 'error', message: err.message || 'Could not submit the resolution.' });
    } finally {
      setBusy(false);
    }
  };

  const doHide = async () => {
    setBusy(true);
    try {
      await hideConditionFor(c.id, viewer.uid);
      if (onChanged) onChanged();
      onClose();
    } catch (err) {
      setNotice({ severity: 'error', message: err.message });
    } finally {
      setBusy(false);
    }
  };

  const history = Array.isArray(c.statusHistory) ? [...c.statusHistory].reverse() : [];
  // Tim 20 Oct: a centered modal, not a side drawer.
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" fullScreen={fullScreen} PaperProps={{ sx: { maxHeight: fullScreen ? '100%' : '90vh' } }}>
      <Box sx={{ p: { xs: 2, sm: 3 }, display: 'flex', flexDirection: 'column', height: '100%', minHeight: fullScreen ? '100%' : 360 }}>
        <Stack direction="row" alignItems="flex-start" spacing={1} sx={{ mb: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ConditionHeader condition={c} org={viewer?.org} />
          </Box>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
        <SeverityChips condition={c} />
        <InlineNotice notice={notice} onClose={() => setNotice(null)} />

        <Box sx={{ flex: 1, overflowY: 'auto', mt: 2, minHeight: 0 }}>
          <Typography variant="overline" color="text.secondary">Description</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
            {c.description}
          </Typography>
          {(c.driftType || c.trapCategory) && (
            <Typography variant="body2" sx={{ mb: 2 }}>
              <strong>{conditionTypeLabel(c.type)}:</strong> {c.type === 'drift' ? driftTypeLabel(c.driftType) : trapCategoryLabel(c.trapCategory)}
            </Typography>
          )}
          {(c.refActions || []).length > 0 && (
            <>
              <Typography variant="overline" color="text.secondary">REF Actions</Typography>
              <Stack spacing={0.5} sx={{ mb: 2 }}>
                {c.refActions.map((a, i) => (
                  <Typography key={i} variant="body2">
                    <Chip size="small" label={refActionLabel(a.type).toUpperCase()} variant="outlined" sx={{ mr: 1, fontWeight: 700 }} />
                    {a.notes}
                    {a.addedByName ? ` — ${a.addedByName}` : ''}
                  </Typography>
                ))}
              </Stack>
            </>
          )}
          {c.l2?.revisedRefNotes && (
            <Alert severity="info" sx={{ mb: 2 }}>
              L2 revision: {c.l2.revisedRefNotes}
            </Alert>
          )}
          {c.resolution?.note && (
            <>
              <Typography variant="overline" color="text.secondary">Resolution</Typography>
              <Typography variant="body2" sx={{ mb: 0.5 }}>
                <strong>{c.resolution.submittedByName || 'Owner'}:</strong> {c.resolution.note}
              </Typography>
              <Stack direction="row" spacing={0.75} sx={{ mb: 2 }} useFlexGap flexWrap="wrap">
                <Chip size="small" label={c.resolution.l1ApprovedAt ? 'L1 approved' : 'Awaiting L1'} color={c.resolution.l1ApprovedAt ? 'success' : 'default'} />
                <Chip size="small" label={c.resolution.l2ApprovedAt ? 'L2 approved' : c.status === 'resolved' ? 'Closed' : 'Awaiting L2'} color={c.resolution.l2ApprovedAt || c.status === 'resolved' ? 'success' : 'default'} />
              </Stack>
              {c.resolution.returnNote && c.status !== 'resolved' && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Returned: {c.resolution.returnNote}
                </Alert>
              )}
            </>
          )}
          {c.rejectionReason && c.status === 'rejected' && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Not Approved: {c.rejectionReason}
            </Alert>
          )}
          {c.cancelReason && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Cancelled by {c.cancelledByName || 'an administrator'}: {c.cancelReason}
            </Alert>
          )}
          <Divider sx={{ my: 1.5 }} />
          <Typography variant="overline" color="text.secondary">History</Typography>
          <Stack spacing={0.5}>
            {history.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Logged {when(c.submittedAt)}
              </Typography>
            )}
            {history.map((h, i) => (
              <Typography key={i} variant="body2" color="text.secondary">
                <strong>{conditionStatusLabel(h.status)}</strong> · {when(h.at)}
                {h.byName ? ` · ${h.byName}` : ''}
                {h.actingFor?.name ? ` (entered by an administrator for ${h.actingFor.name})` : ''}
                {h.note ? ` — ${h.note}` : ''}
              </Typography>
            ))}
          </Stack>
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <Stack direction="row" spacing={1.5} justifyContent="flex-end" alignItems="center" useFlexGap flexWrap="wrap">
          {mayHide && (
            <Button size="medium" color="inherit" startIcon={<DeleteOutlineIcon />} disabled={busy} onClick={doHide}>
              Delete from my list
            </Button>
          )}
          {mayEdit && (
            <Button size="large" variant="outlined" startIcon={<EditIcon />} sx={{ px: 3 }} onClick={() => { onClose(); navigate(`/drew/rollup/trl-entry?edit=${c.id}`); }}>
              Edit
            </Button>
          )}
          {mayResolve && (
            <Button size="large" variant="contained" color="success" startIcon={<TaskAltIcon />} sx={{ px: 3 }} disabled={busy} onClick={() => setResolveOpen(true)}>
              Mark as Resolved
            </Button>
          )}
        </Stack>
      </Box>
      <ResolveDialog open={resolveOpen} condition={c} direct={direct} busy={busy} onClose={() => setResolveOpen(false)} onConfirm={doResolve} />
    </Dialog>
  );
}

export default ConditionDetailDrawer;
